/**
 *  @fileOverview Utilities helpers
 *
 *  @author       Chris Hill
 *
 */

export default class {
  static polyfills () {
    /**
     * @description Polyfills to provide support to IE11.
     * @example
     * //
     */

    /**
     * Polyfill to provide Node.forEach() support to IE11.
     * https://developer.mozilla.org/en-US/docs/Web/API/NodeList/forEach.
     */

    /* eslint-disable-next-line no-undef */
    if (window.NodeList && !NodeList.prototype.forEach) {
      /* eslint-disable-next-line no-undef */
      NodeList.prototype.forEach = Array.prototype.forEach
    }

    /**
     * Polyfill to provide Element.closest() support to IE11.
     * https://developer.mozilla.org/en-US/docs/Web/API/Element/closest
     */

    /* eslint-disable-next-line no-undef */
    if (!Element.prototype.matches) {
      /* eslint-disable-next-line no-undef */
      Element.prototype.matches = Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector
    }

    /* eslint-disable-next-line no-undef */
    if (!Element.prototype.closest) {
      /* eslint-disable-next-line no-undef */
      Element.prototype.closest = function (s) {
        var el = this

        do {
          if (el.matches(s)) return el
          el = el.parentElement || el.parentNode
        } while (el !== null && el.nodeType === 1)
        return null
      }
    }

    /**
     * Polyfill to provide Element.remove() support to IE11.
     * https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove
     */

    (function (arr) {
      arr.forEach(function (item) {
        if (item.hasOwnProperty('remove')) {
          return
        }
        Object.defineProperty(item, 'remove', {
          configurable: true,
          enumerable: true,
          writable: true,
          value: function remove () {
            this.parentNode.removeChild(this)
          }
        })
      })
    })([Element.prototype, CharacterData.prototype, DocumentType.prototype]); // semicolon required to separate self calling functions https://stackoverflow.com/questions/20671614/error-with-multiple-self-invoking-functions-in-one-script-a-case-for-a-semicolo

    /**
     * Polyfill to provide customEvent support to IE11.
     * https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent#Polyfill
     */

    /* eslint-disable-next-line no-unexpected-multiline */
    (function () {
      if (typeof window.CustomEvent === 'function') return false

      function CustomEvent (event, params) {
        params = params || { bubbles: false, cancelable: false, detail: undefined }
        var evt = document.createEvent('CustomEvent')
        evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail)
        return evt
      }

      CustomEvent.prototype = window.Event.prototype

      window.CustomEvent = CustomEvent
    })()
  }

  /**
   * @description Function to find all siblings of a node.
   *
   * @param {Object} n - The DOM node to find siblings of.
   *
   * @returns {Array} - The array of sibling elements.
   * @example
   * //
   */
  static getSiblings (n) {
    return getChildren(n.parentNode.firstChild, n)

    function getChildren (n, skipMe) {
      var r = []
      for (; n; n = n.nextSibling) {
        if (n.nodeType === 1 && n !== skipMe) {
          r.push(n)
        }
      }
      return r
    }
  }

  /**
   * @description Function to create a promise delay for async timeouts.
   *
   * @param {number} timer - The length to create for the timeout.
   *
   * @returns {Promise} - The resulting promise.
   * @example
   * //
   */
  static delay (timer) {
    return new Promise(resolve => {
      timer = timer || 2000
      setTimeout(function () {
        resolve()
      }, timer)
    })
  }

  /**
   * @description Debounce for performance.
   * Credit David Walsh (https://davidwalsh.name/javascript-debounce-function)
   * Returns a function, that, as long as it continues to be invoked, will not
   * be triggered. The function will be called after it stops being called for
   * N milliseconds. If `immediate` is passed, trigger the function on the
   * leading edge, instead of the trailing.
   *
   * @param {Object} func - The function to apply debounce too.
   * @param {number} wait - The delay on running the function.
   * @param {boolean} immediate - No delay.
   * @example
   * //
   * @returns {Object} The function that is to be run.
   */
  static debounce (func, wait, immediate) {
    var timeout

    return function executedFunction () {
      var context = this
      var args = arguments

      var later = function () {
        timeout = null
        if (!immediate) func.apply(context, args)
      }

      var callNow = immediate && !timeout

      clearTimeout(timeout)

      timeout = setTimeout(later, wait)

      if (callNow) func.apply(context, args)
    }
  }
}
